/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Booking page body, a faithful clone of aegis-ao-rental_web BookPage (blue palette, light
 * gray-50 page, 2-column layout: vehicle card on the left, a sticky "Booking Details" card on
 * the right with date + TIME pickers, a rate line and a days/total summary). Routes the booking
 * to the tenant owner's booking plugin (e.g. HQ Rental — payment runs through HQ's own gateway)
 * when configured, otherwise the native Aegis flow. The site header stays black.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Clock, ExternalLink, CheckCircle2 } from 'lucide-react';
import type { Car, BookingInfo, AdditionalServiceOption } from '../../types';
import { addOnLineAmount, cadenceSuffix, stripHtml } from '../../utils/addOnPricing';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import BookingAuthModal from './BookingAuthModal';
import AgreementModal from './AgreementModal';
import CardModal from './CardModal';
import ModelImage from '../ModelImage';
import AvailabilityCalendar from './AvailabilityCalendar';
import { fmtPrice, currencySymbol, selectTierForDays, tierTotal, type RateTiers } from '../../utils/pricing';
import { categoryLabel, pickLocalized } from '../../utils/localized';

const DEFAULT_PICKUP_TIME = '10:00';
const DEFAULT_RETURN_TIME = '10:00';

function isoDate(offsetDays: number): string {
  return new Date(Date.now() + offsetDays * 864e5).toISOString().slice(0, 10);
}

/** Use a supplied yyyy-MM-dd date when it's valid and not in the past, else the default. */
function seedDate(candidate: string | undefined, fallbackOffset: number): string {
  const s = (candidate || '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s) && s >= isoDate(0)) return s;
  return isoDate(fallbackOffset);
}

/** Number of rental days (min 1), rounded up, like rental_web's usePricing. */
function rentalDays(fromDate: string, fromTime: string, toDate: string, toTime: string): number {
  const a = new Date(`${fromDate}T${fromTime || DEFAULT_PICKUP_TIME}:00`);
  const b = new Date(`${toDate}T${toTime || DEFAULT_RETURN_TIME}:00`);
  const hours = (b.getTime() - a.getTime()) / 36e5;
  return Math.max(1, Math.ceil(hours / 24));
}


export default function BookingView({
  car: initialCar, availableCount: initialCount, modelMake, modelModel, modelCity, modelOwnerId,
  initialFrom, initialTo,
}: {
  car: Car;
  availableCount?: number;
  /** When booking a MODEL, the dates re-resolve the first free car of this make/model. */
  modelMake?: string;
  modelModel?: string;
  modelCity?: string;
  /** When a company was picked, restrict the model re-resolve to that rental company. */
  modelOwnerId?: string;
  /** Pre-fill the date range with what the renter searched (yyyy-MM-dd), so the seasonal price
   *  shown on the result card carries through to the booking. Falls back to tomorrow … +7 days. */
  initialFrom?: string;
  initialTo?: string;
}) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const isModel = !!modelModel;

  const [car, setCar] = useState<Car>(initialCar);
  const [count, setCount] = useState(initialCount ?? 0);
  const [unavailable, setUnavailable] = useState(false);
  const [checking, setChecking] = useState(false);

  const [info, setInfo] = useState<BookingInfo | null>(null);
  const [pickupDate, setPickupDate] = useState(() => seedDate(initialFrom, 1));
  const [pickupTime, setPickupTime] = useState(DEFAULT_PICKUP_TIME);
  const [returnDate, setReturnDate] = useState(() => {
    const from = seedDate(initialFrom, 1);
    const to = seedDate(initialTo, 8);
    return to > from ? to : isoDate(8); // guard against a same-day/inverted searched range
  });
  const [returnTime, setReturnTime] = useState(DEFAULT_RETURN_TIME);

  const [busy, setBusy] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [nativeBookingId, setNativeBookingId] = useState('');
  const [showAgreement, setShowAgreement] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [cardForBooking, setCardForBooking] = useState(false); // CardModal is gating booking creation
  const [done, setDone] = useState<{ paymentLink?: string; paid?: boolean; plugin?: boolean; native?: boolean; needsCard?: boolean } | null>(null);
  const [error, setError] = useState('');
  const [busyDays, setBusyDays] = useState<Set<string>>(new Set());
  // Additional services (extras): id → selected QUANTITY (0 = not selected). Mandatory ones are forced
  // to ≥1 (see effect below). Optional ones toggle 0↔1 and can be stepped up.
  const [selectedSvc, setSelectedSvc] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!car?.id) return;
    // Pass the rental start + end dates so the price reflects the active season and any duration intervals.
    const dateFrom = pickupDate ? new Date(`${pickupDate}T${pickupTime}`).toISOString() : undefined;
    const dateTo = returnDate ? new Date(`${returnDate}T${returnTime}`).toISOString() : undefined;
    api.getBookingInfo(car.id, dateFrom, dateTo).then(setInfo).catch(() => setInfo(null));
  }, [car?.id, pickupDate, pickupTime, returnDate, returnTime]);

  // Pre-select the owner's mandatory services when booking info loads.
  useEffect(() => {
    const svcs = info?.services;
    if (!svcs || svcs.length === 0) return;
    const m: Record<string, number> = {};
    for (const s of svcs) if (s.isMandatory) m[s.additionalServiceId] = 1;
    setSelectedSvc(m);
  }, [info?.services]);

  // Availability calendar: a car's booked days, or (for a model) days where every car is booked.
  useEffect(() => {
    const from = isoDate(0);
    const to = new Date(Date.now() + 180 * 864e5).toISOString().slice(0, 10);
    const req = isModel
      ? { make: modelMake, model: modelModel, city: modelCity, ownerId: modelOwnerId, from, to }
      : { carId: initialCar.id, from, to };
    api.getAvailabilityCalendar(req).then(setBusyDays).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModel, modelMake, modelModel, modelCity, modelOwnerId, initialCar.id]);

  // Re-check availability for the SELECTED dates: model pages re-resolve the first free car (+ count),
  // a specific-car page re-checks that one car. Keeps the green badge and the submit gate honest.
  useEffect(() => {
    if (!pickupDate || !returnDate) { setUnavailable(false); return; } // mid-selection (only start picked)
    const from = new Date(`${pickupDate}T${pickupTime}`);
    const to = new Date(`${returnDate}T${returnTime}`);
    if (to <= from) { setUnavailable(true); return; }
    const dateFrom = from.toISOString();
    const dateTo = to.toISOString();
    let cancelled = false;
    setChecking(true);
    (async () => {
      try {
        if (isModel) {
          const { car: c, availableCount } = await api.resolveModelCar({
            make: modelMake, model: modelModel!, city: modelCity, ownerId: modelOwnerId, dateFrom, dateTo,
          });
          if (cancelled) return;
          setCount(availableCount);
          if (c) { setCar(c); setUnavailable(false); } else setUnavailable(true);
        } else {
          const ok = await api.checkAvailability(initialCar.id, dateFrom, dateTo);
          if (cancelled) return;
          setUnavailable(!ok);
        }
      } catch {
        // Fail open — never block the renter on a check error.
        if (!cancelled) setUnavailable(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupDate, pickupTime, returnDate, returnTime, isModel, modelMake, modelModel, modelCity, modelOwnerId, initialCar.id]);

  const fallbackPrice = info?.price ?? car.priceFullTimePerWeek ?? car.pricePerWeek ?? 0;
  const fallbackPeriod = info?.pricingPeriod ?? car.pricingPeriod;
  // Per-period tiers come from booking-info when available, else the car listing.
  const tiers: RateTiers = {
    priceDaily: info?.priceDaily ?? car.priceDaily,
    priceWeekly: info?.priceWeekly ?? car.priceWeekly,
    priceMonthly: info?.priceMonthly ?? car.priceMonthly,
  };
  const make = (car.brand || '').trim();
  const model = (car.model || '').trim();
  const titleLine = `${make} ${model}`.trim() || car.title || t('booking.vehicle');
  const category = categoryLabel(car, i18n.language);

  const days = useMemo(
    () => (pickupDate && returnDate ? rentalDays(pickupDate, pickupTime, returnDate, returnTime) : 0),
    [pickupDate, pickupTime, returnDate, returnTime],
  );
  // Pick the rate tier matching the rental length (mirrors the backend), then total over the window.
  const sel = useMemo(
    () => selectTierForDays(tiers, fallbackPrice, fallbackPeriod, days || 1),
    [tiers.priceDaily, tiers.priceWeekly, tiers.priceMonthly, fallbackPrice, fallbackPeriod, days],
  );
  const price = sel.amount;
  const unit = t(`units.${sel.unit}`);
  const total = useMemo(() => tierTotal(sel.amount, sel.periodDays, days), [sel, days]);
  const cur = currencySymbol(info?.currency);
  // Effective quantity for a service: mandatory ones are at least 1; optional ones use the selected qty (0 = off).
  const svcQty = (s: AdditionalServiceOption) =>
    s.isMandatory ? Math.max(1, selectedSvc[s.additionalServiceId] || 1) : (selectedSvc[s.additionalServiceId] || 0);
  // Extras: amount per cadence (per_day/per_hour/per_week/per_month/per_minute/percent/one_time) × quantity.
  // Mandatory always counts (qty ≥ 1). Percent is of the rental subtotal. Server recomputes the real charge.
  const servicesTotal = useMemo(() => {
    const svcs = info?.services ?? [];
    const d = Math.max(1, days || 1);
    return svcs.reduce((sum, s) => {
      const qty = s.isMandatory ? Math.max(1, selectedSvc[s.additionalServiceId] || 1) : (selectedSvc[s.additionalServiceId] || 0);
      if (qty <= 0) return sum;
      return sum + addOnLineAmount(s.pricingType, Number(s.price) || 0, qty, d, total);
    }, 0);
  }, [info?.services, selectedSvc, days, total]);
  const grandTotal = total + servicesTotal;
  const serviceSelections = () => (info?.services ?? [])
    .map((s) => ({ id: s.additionalServiceId, qty: svcQty(s) }))
    .filter((x) => x.qty > 0)
    .map((x) => ({ additionalServiceId: x.id, quantity: x.qty }));
  // Toggle an optional service on/off (0↔1); step its quantity up/down (min 1 while selected).
  const toggleSvc = (id: string) =>
    setSelectedSvc((prev) => ({ ...prev, [id]: (prev[id] || 0) > 0 ? 0 : 1 }));
  const stepSvc = (id: string, delta: number) =>
    setSelectedSvc((prev) => ({ ...prev, [id]: Math.max(1, (prev[id] || 1) + delta) }));
  const invalidDates = !pickupDate || !returnDate
    || new Date(`${returnDate}T${returnTime}`) <= new Date(`${pickupDate}T${pickupTime}`);

  const submit = async (skipAuthCheck = false) => {
    // Owner collects through a booking plugin that's unavailable → booking is blocked. Native is
    // forbidden for this owner, so we never silently fall back to it.
    if (info?.bookingPluginUnavailable) {
      setError(t('booking.pluginUnavailable', 'This rental company processes bookings through its own system, which is temporarily unavailable. Please try again shortly.'));
      return;
    }
    if (!skipAuthCheck && !isAuthenticated) {
      setShowLogin(true);
      return;
    }
    setBusy(true);
    setError('');
    setDone(null);
    const dateFrom = new Date(`${pickupDate}T${pickupTime}`).toISOString();
    const dateTo = new Date(`${returnDate}T${returnTime}`).toISOString();

    try {
      if (info?.usesBookingPlugin) {
        // Plugin collects payment through its own gateway — no card-on-file step here.
        const res = await api.createPluginBooking({ carId: car.id, dateFrom, dateTo, additionalServices: serviceSelections() });
        if (!res.ok) setError(res.message || t('booking.failed'));
        else setDone({ plugin: true, paymentLink: res.paymentLink, paid: res.status === 'paid' });
      } else {
        // Native: the backend REQUIRES a card on file before a booking can be created. If the renter
        // has none, collect it FIRST (CardModal), then create the booking — never the other way round.
        // Cards are per rental company: look in THIS car's owner's card set.
        const cards = await api.getCards(api.getStoredUser()?.id || '', { carId: car.id, ownerId: car.ownerId });
        if (cards.length === 0) {
          setCardForBooking(true);
          setShowCard(true);
          return; // CardModal.onDone re-runs submit() once the card is on file
        }
        await createNativeBooking(dateFrom, dateTo);
      }
    } finally {
      setBusy(false);
    }
  };

  // Create the native booking (card already on file). Then sign the agreement if the owner requires it.
  const createNativeBooking = async (dateFrom: string, dateTo: string) => {
    const res = await api.createCompleteBooking({ carId: car.id, dateFrom, dateTo, rentalType: 2, carTypeId: '', additionalServices: serviceSelections() });
    if (!res.ok) { setError(res.message || t('booking.failed')); return; }
    const bid = res.bookingId || '';
    setNativeBookingId(bid);
    if (bid && info?.requiresAgreement) setShowAgreement(true);
    else setDone({ native: true, needsCard: false });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-blue-600 mb-6">
          <ArrowLeft className="h-5 w-5 mr-2" /> {t('detail.backToSearch')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] gap-8">
          {/* Left — vehicle */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="relative">
                <ModelImage make={car.brand} model={car.model} fallback={car.imageURL} alt={titleLine} className="w-full h-64 object-cover" carImageFirst />
                <div className={`absolute top-3 right-3 text-white text-xs font-bold px-3 py-1 rounded-full shadow ${unavailable ? 'bg-red-500' : 'bg-green-500'}`}>
                  {unavailable
                    ? t('booking.unavailable')
                    : isModel
                      ? t('booking.availableCount', { n: count })
                      : t('booking.available')}
                </div>
              </div>
              <div className="p-6 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">{titleLine}</p>
                <h1 className="text-3xl font-bold text-gray-900">{t('booking.bookModel', { name: titleLine })}</h1>
                {price > 0 && <p className="text-xl font-semibold text-blue-600">{cur}{fmtPrice(price)} / {unit}</p>}
                {category && <p className="text-sm font-semibold uppercase text-gray-700">{category}</p>}
                {car.description && <p className="text-gray-600 whitespace-pre-line">{car.description}</p>}
              </div>
            </div>
          </div>

          {/* Right — booking form */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6 lg:sticky lg:top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('booking.title')}</h2>

              {done ? (
                <BookingDone done={done} title={titleLine} />
              ) : (
                <>
                  <div className="mb-6">
                    {/* Pick a free interval on the calendar; busy days are disabled. */}
                    <AvailabilityCalendar
                      busyDays={busyDays}
                      from={pickupDate}
                      to={returnDate}
                      minDate={isoDate(0)}
                      onChange={(f, tt) => { setPickupDate(f); setReturnDate(tt); }}
                    />
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <TimeField label={t('booking.pickup')} value={pickupTime} onChange={setPickupTime} />
                      <TimeField label={t('booking.dropoff')} value={returnTime} onChange={setReturnTime} />
                    </div>
                  </div>

                  {price > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-1">{cur}{fmtPrice(price)} / {unit}</p>
                      <h3 className="text-md font-semibold text-gray-900">{t('booking.rateTitle', { unit })}</h3>
                    </div>
                  )}

                  {(info?.services?.length ?? 0) > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('booking.extras', 'Additional options')}</h3>
                      <div className="space-y-2">
                        {info!.services!.map((s: AdditionalServiceOption) => {
                          const qty = svcQty(s);
                          const checked = qty > 0;
                          const suffix = cadenceSuffix(s.pricingType, (k, d) => t(k, d ? { defaultValue: d } : undefined) as string);
                          const svcName = pickLocalized(s.nameJson, s.name, i18n.language) || s.name;
                          const svcDesc = stripHtml(pickLocalized(s.descriptionJson, s.description, i18n.language));
                          return (
                            <div key={s.additionalServiceId} className="flex items-center justify-between gap-3 text-sm text-gray-700">
                              <label className="flex items-center gap-3 flex-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={s.isMandatory}
                                  onChange={() => toggleSvc(s.additionalServiceId)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span>
                                  {svcName}: {cur}{fmtPrice(Number(s.price) || 0)} {suffix}
                                  {svcDesc && <span className="block text-xs text-gray-400 font-normal">{svcDesc}</span>}
                                </span>
                              </label>
                              {checked && !s.isMandatory && (
                                <div className="flex items-center gap-2 shrink-0">
                                  <button
                                    type="button"
                                    aria-label={t('booking.decrease', 'Decrease')}
                                    onClick={() => stepSvc(s.additionalServiceId, -1)}
                                    className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                                    disabled={qty <= 1}
                                  >−</button>
                                  <span className="w-5 text-center tabular-nums">{qty}</span>
                                  <button
                                    type="button"
                                    aria-label={t('booking.increase', 'Increase')}
                                    onClick={() => stepSvc(s.additionalServiceId, 1)}
                                    className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                                  >+</button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('booking.days', { n: days })}</span>
                      <span className="text-xl font-bold text-blue-600">{cur}{fmtPrice(grandTotal)}</span>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-sm font-semibold text-gray-900">{t('booking.total')}</span>
                      <span className="text-xl font-bold text-blue-600">{cur}{fmtPrice(grandTotal)}</span>
                    </div>
                  </div>

                  {unavailable && !invalidDates && (
                    <div className="mb-3 text-sm rounded-lg bg-red-50 text-red-700 px-3 py-2">
                      {isModel ? t('booking.noneForPeriod', { model: modelModel }) : t('booking.unavailableDates')}
                    </div>
                  )}
                  {info?.bookingPluginUnavailable && (
                    <div className="mb-3 text-sm rounded-lg bg-red-50 text-red-700 px-3 py-2">
                      {t('booking.pluginUnavailable', 'This rental company processes bookings through its own system, which is temporarily unavailable. Please try again shortly.')}
                    </div>
                  )}
                  {error && <div className="mb-3 text-sm rounded-lg bg-amber-50 text-amber-800 px-3 py-2">{error}</div>}

                  <button
                    onClick={() => submit()}
                    disabled={busy || invalidDates || unavailable || checking || info?.bookingPluginUnavailable}
                    className="btn-primary w-full py-3 text-lg"
                  >
                    {checking ? t('booking.checking') : busy ? t('booking.processing') : t('booking.completeBooking')}
                  </button>

                  {!isAuthenticated && (
                    <p className="text-sm text-center text-gray-600 mt-3">{t('booking.signInToComplete')}</p>
                  )}
                  <p className="mt-2 text-xs text-gray-400 text-center">{t('booking.note')}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {showLogin && <BookingAuthModal onClose={() => setShowLogin(false)} onAuthed={() => submit(true)} />}

      {showAgreement && nativeBookingId && (
        <AgreementModal
          bookingId={nativeBookingId}
          onClose={() => { setShowAgreement(false); setDone({ native: true, needsCard: false }); }}
          onSigned={() => { setShowAgreement(false); setDone({ native: true, needsCard: false }); }}
        />
      )}

      {showCard && (
        <CardModal
          carId={car.id}
          ownerId={car.ownerId}
          onClose={() => {
            setShowCard(false);
            if (cardForBooking) { setCardForBooking(false); setError(t('booking.cardRequired', 'A payment card is required to book. Please add one to continue.')); }
            else setDone({ native: true, needsCard: true });
          }}
          onDone={() => {
            setShowCard(false);
            if (cardForBooking) { setCardForBooking(false); submit(true); } // card on file → now create the booking
            else setDone({ native: true, needsCard: false });
          }}
        />
      )}
    </div>
  );
}

function TimeField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="time"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-10 pr-2 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 text-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
        />
      </div>
    </div>
  );
}

function BookingDone({ done, title }: { done: { paymentLink?: string; paid?: boolean; plugin?: boolean; native?: boolean; needsCard?: boolean }; title: string }) {
  const { t } = useTranslation();
  return (
    <div className="text-center py-4">
      <CheckCircle2 className="mx-auto text-green-500" size={48} />
      <h3 className="mt-3 text-lg font-bold text-gray-900">{done.paid ? t('booking.confirmed') : t('booking.requested')}</h3>
      <p className="mt-1 text-sm text-gray-600">{t('booking.requestedFor', { car: title })}</p>

      {/* Plugin (e.g. HQ Rental): payment goes through the rental company's own gateway. */}
      {done.paid ? (
        <p className="mt-4 text-sm text-green-700">{t('booking.paidViaPlatform')}</p>
      ) : done.paymentLink ? (
        <>
          {/* No card on file in HQ → its hosted add-card + pay page. */}
          <a href={done.paymentLink} target="_blank" rel="noreferrer"
            className="btn-primary mt-4 inline-flex items-center gap-2 py-3 px-5">
            {t('booking.addCardAndPay')} <ExternalLink size={16} />
          </a>
          <p className="mt-2 text-xs text-gray-400">{t('booking.payViaPlatform')}</p>
        </>
      ) : done.plugin ? (
        <p className="mt-4 text-sm text-gray-600">{t('booking.paymentPending')}</p>
      ) : null}

      {done.native && (
        <p className="mt-4 text-sm text-gray-600">
          {done.needsCard ? t('booking.nextStepsCard') : t('booking.nextSteps')}
        </p>
      )}
    </div>
  );
}
